import { ThemeEvents } from '@theme/events';

const drawerSelector = '.cart-drawer__dialog';
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const cartState = {
  isOpen: false,
  knownKeys: new Set(),
  pendingSource: '',
  reconcileFrame: 0,
};

const animations = new WeakMap();

function animate(element, keyframes, options) {
  if (!(element instanceof HTMLElement) || reduceMotion.matches) return null;

  animations.get(element)?.cancel();
  const animation = element.animate(keyframes, { fill: 'both', ...options });
  animations.set(element, animation);
  animation.finished
    .then(() => animation.cancel())
    .catch(() => {})
    .finally(() => {
      if (animations.get(element) === animation) animations.delete(element);
    });
  return animation;
}

function getDrawer() {
  return document.querySelector(drawerSelector);
}

function getOpenDrawer() {
  const drawer = getDrawer();
  return drawer instanceof HTMLDialogElement && drawer.open ? drawer : null;
}

function collectCartKeys(root = document) {
  return new Set(
    Array.from(root.querySelectorAll('.cart-drawer__dialog .cart-items__table-row[data-key]'))
      .map((row) => row.dataset.key)
      .filter(Boolean)
  );
}

function animateDrawerEntrance(drawer) {
  const ease = 'cubic-bezier(0.16, 1, 0.3, 1)';
  const sequence = [
    { selector: '.cart-drawer__header', delay: 35, y: -14, scale: 1 },
    { selector: '.lngvty-cart-motivator', delay: 60, y: -17, scale: 0.98, rotateX: -5 },
    { selector: '.cart-drawer__items', delay: 95, y: 18, scale: 0.995 },
    { selector: '.lngvty-cart-upsells__heading-row', delay: 140, y: 14, scale: 1 },
    { selector: '.cart-drawer__summary', delay: 205, y: 22, scale: 1 },
  ];

  sequence.forEach(({ selector, delay, y, scale, rotateX = 0 }) => {
    const element = drawer.querySelector(selector);
    animate(
      element,
      [
        { opacity: 0, transform: `translate3d(0, ${y}px, 0) scale(${scale}) rotateX(${rotateX}deg)` },
        { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1) rotateX(0deg)' },
      ],
      { duration: 360, delay, easing: ease }
    );
  });

  drawer.querySelectorAll('.cart-items__table-row').forEach((row, index) => {
    animate(
      row,
      [
        { opacity: 0, transform: 'translate3d(28px, 0, 0) scale(0.98)' },
        { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
      ],
      { duration: 320, delay: 120 + index * 40, easing: ease }
    );
  });

  drawer.querySelectorAll('.lngvty-cart-upsells__card').forEach((card, index) => {
    animate(
      card,
      [
        { opacity: 0, transform: 'translate3d(24px, 0, 0) scale(0.96)' },
        { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
      ],
      { duration: 340, delay: 185 + index * 55, easing: ease }
    );
  });
}

function animateNewLine(row) {
  animate(
    row,
    [
      { opacity: 0, transform: 'translate3d(42px, -6px, 0) scale(0.94)' },
      { opacity: 1, transform: 'translate3d(-4px, 0, 0) scale(1.015)', offset: 0.72 },
      { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
    ],
    { duration: 420, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
  );
}

function animateCartFeedback(drawer) {
  drawer.querySelectorAll('.cart__total-value, .cart-items__price text-component').forEach((value, index) => {
    animate(
      value,
      [
        { transform: 'translateY(0) scale(1)', filter: 'brightness(1)' },
        { transform: 'translateY(-3px) scale(1.07)', filter: 'brightness(1.12)', offset: 0.45 },
        { transform: 'translateY(0) scale(1)', filter: 'brightness(1)' },
      ],
      { duration: 280, delay: index * 16, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }
    );
  });

  const count = drawer.querySelector('.cart-drawer__heading small');
  animate(
    count,
    [
      { transform: 'scale(1) rotate(0deg)' },
      { transform: 'scale(1.22) rotate(-3deg)', offset: 0.45 },
      { transform: 'scale(1) rotate(0deg)' },
    ],
    { duration: 300, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }
  );

  document.querySelectorAll('.cart-bubble').forEach((bubble) => {
    animate(
      bubble,
      [
        { transform: 'scale(1)', filter: 'brightness(1)' },
        { transform: 'scale(1.24)', filter: 'brightness(1.25)', offset: 0.45 },
        { transform: 'scale(1)', filter: 'brightness(1)' },
      ],
      { duration: 320, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }
    );
  });
}

function showToast(drawer, message) {
  if (!message) return;

  drawer.querySelector('.lngvty-cart-toast')?.remove();
  const toast = document.createElement('div');
  toast.className = 'lngvty-cart-toast';
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.innerHTML = `<span aria-hidden="true">&#10003;</span><span></span>`;
  const label = toast.lastElementChild;
  if (label) label.textContent = message;
  drawer.append(toast);

  if (reduceMotion.matches) {
    window.setTimeout(() => toast.remove(), 1700);
    return;
  }

  const toastAnimation = toast.animate(
    [
      { opacity: 0, transform: 'translate3d(0, 18px, 0) scale(0.96)' },
      { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)', offset: 0.18 },
      { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)', offset: 0.82 },
      { opacity: 0, transform: 'translate3d(0, 10px, 0) scale(0.98)' },
    ],
    { duration: 1900, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
  );
  toastAnimation.finished.catch(() => {}).finally(() => toast.remove());
}

function reconcileCart() {
  cartState.reconcileFrame = 0;
  const drawer = getOpenDrawer();
  const nextKeys = collectCartKeys();
  if (!drawer) {
    cartState.knownKeys = nextKeys;
    return;
  }

  drawer.querySelectorAll('.cart-items__table-row[data-key]').forEach((row) => {
    const key = row.dataset.key;
    if (key && !cartState.knownKeys.has(key)) animateNewLine(row);
  });

  animateCartFeedback(drawer);
  if (['lngvty-cart-upsells', 'lngvty-legacy-cart', 'product-form-component', 'enhanced-v2-buybox'].includes(cartState.pendingSource)) {
    showToast(drawer, 'Added to your cart');
  }

  cartState.pendingSource = '';
  cartState.knownKeys = nextKeys;
}

function scheduleReconcile() {
  if (cartState.reconcileFrame) window.cancelAnimationFrame(cartState.reconcileFrame);
  cartState.reconcileFrame = window.requestAnimationFrame(() => {
    cartState.reconcileFrame = window.requestAnimationFrame(reconcileCart);
  });
}

function onCartUpdate(event) {
  cartState.pendingSource = event.detail?.data?.source || '';
  scheduleReconcile();
}

function onDiscountUpdate() {
  cartState.pendingSource = 'discount';
  scheduleReconcile();
}

function mutationContainsCartRender(mutation) {
  const selector = '.cart-items__table-row, .cart__summary-totals, lngvty-cart-motivator, lngvty-cart-upsells';
  return [...mutation.addedNodes, ...mutation.removedNodes].some(
    (node) => node instanceof Element && (node.matches(selector) || Boolean(node.querySelector(selector)))
  );
}

function onQuantityUpdate(event) {
  const drawer = getOpenDrawer();
  if (!drawer || !(event.target instanceof Element) || !drawer.contains(event.target)) return;

  const input = event.target.querySelector('input[data-cart-quantity]');
  const row = event.target.closest('.cart-items__table-row');
  const quantity = Number(event.detail?.quantity);
  const previousQuantity = Number(input?.dataset.cartQuantity || input?.defaultValue || quantity);
  const direction = quantity >= previousQuantity ? 1 : -1;

  animate(
    input,
    [
      { opacity: 1, transform: 'translateY(0)' },
      { opacity: 0, transform: `translateY(${-8 * direction}px)`, offset: 0.46 },
      { opacity: 0, transform: `translateY(${8 * direction}px)`, offset: 0.47 },
      { opacity: 1, transform: 'translateY(0)' },
    ],
    { duration: 230, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
  );

  const thumbnail = row?.querySelector('.cart-items__media-container');
  animate(
    thumbnail,
    [
      { transform: 'rotate(0deg) scale(1)' },
      { transform: `rotate(${3 * direction}deg) scale(1.04)`, offset: 0.42 },
      { transform: `rotate(${-1.5 * direction}deg) scale(1.01)`, offset: 0.72 },
      { transform: 'rotate(0deg) scale(1)' },
    ],
    { duration: 300, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }
  );
}

function addRipple(button, event) {
  if (reduceMotion.matches) return;

  const rect = button.getBoundingClientRect();
  const ripple = document.createElement('span');
  const size = Math.max(rect.width, rect.height) * 1.5;
  const clientX = 'clientX' in event && event.clientX ? event.clientX : rect.left + rect.width / 2;
  const clientY = 'clientY' in event && event.clientY ? event.clientY : rect.top + rect.height / 2;

  ripple.className = 'lngvty-button-ripple';
  ripple.style.width = `${size}px`;
  ripple.style.height = `${size}px`;
  ripple.style.left = `${clientX - rect.left - size / 2}px`;
  ripple.style.top = `${clientY - rect.top - size / 2}px`;
  button.classList.add('lngvty-ripple-host');
  button.append(ripple);
  ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
}

function onDrawerClick(event) {
  const drawer = getOpenDrawer();
  if (!drawer || !(event.target instanceof Element) || !drawer.contains(event.target)) return;

  const button = event.target.closest('button');
  if (button instanceof HTMLButtonElement) addRipple(button, event);

  const removeButton = event.target.closest('.cart-items__remove');
  const row = removeButton?.closest('.cart-items__table-row');
  if (row) {
    animate(
      row,
      [
        { opacity: 1, transform: 'translate3d(0, 0, 0) rotate(0deg)' },
        { opacity: 0, transform: 'translate3d(54px, -4px, 0) rotate(1.5deg)' },
      ],
      { duration: 230, easing: 'cubic-bezier(0.4, 0, 1, 1)' }
    );
  }

  const checkout = event.target.closest('.cart__checkout-button');
  if (checkout instanceof HTMLButtonElement && !checkout.disabled && !checkout.classList.contains('is-processing')) {
    const label = checkout.querySelector('.lngvty-checkout-label');
    if (label instanceof HTMLElement) {
      label.dataset.originalLabel = label.textContent?.trim() || 'CHECKOUT';
      label.textContent = 'PROCESSING…';
    }
    checkout.classList.add('is-processing');
    checkout.setAttribute('aria-busy', 'true');
    window.setTimeout(() => {
      if (!checkout.isConnected) return;
      if (label instanceof HTMLElement) label.textContent = label.dataset.originalLabel || 'CHECKOUT';
      checkout.classList.remove('is-processing');
      checkout.removeAttribute('aria-busy');
    }, 7000);
  }
}

function onPointerMove(event) {
  if (reduceMotion.matches || !window.matchMedia('(hover: hover)').matches || !(event.target instanceof Element)) return;
  const card = event.target.closest('.lngvty-cart-upsells__card');
  if (!(card instanceof HTMLElement)) return;

  const rect = card.getBoundingClientRect();
  const x = (event.clientX - rect.left) / rect.width - 0.5;
  const y = (event.clientY - rect.top) / rect.height - 0.5;
  card.style.setProperty('--lngvty-tilt-x', `${(-y * 5).toFixed(2)}deg`);
  card.style.setProperty('--lngvty-tilt-y', `${(x * 6).toFixed(2)}deg`);
  card.style.setProperty('--lngvty-card-x', `${(x * 5).toFixed(2)}px`);
  card.style.setProperty('--lngvty-card-y', `${(y * 4).toFixed(2)}px`);
  card.classList.add('is-tilting');
}

function onPointerOut(event) {
  if (!(event.target instanceof Element)) return;
  const card = event.target.closest('.lngvty-cart-upsells__card');
  if (!(card instanceof HTMLElement) || (event.relatedTarget instanceof Node && card.contains(event.relatedTarget))) return;

  card.classList.remove('is-tilting');
  card.style.removeProperty('--lngvty-tilt-x');
  card.style.removeProperty('--lngvty-tilt-y');
  card.style.removeProperty('--lngvty-card-x');
  card.style.removeProperty('--lngvty-card-y');
}

function handleDrawerState() {
  const drawer = getOpenDrawer();
  const isOpen = Boolean(drawer);
  if (isOpen && !cartState.isOpen) {
    cartState.isOpen = true;
    window.requestAnimationFrame(() => {
      animateDrawerEntrance(drawer);
      cartState.knownKeys = collectCartKeys(drawer);
    });
  } else if (!isOpen && cartState.isOpen) {
    cartState.isOpen = false;
  }
}

if (!globalThis.__lngvtyCartMotionLoaded) {
  globalThis.__lngvtyCartMotionLoaded = true;
  cartState.knownKeys = collectCartKeys();

  document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);
  document.addEventListener(ThemeEvents.discountUpdate, onDiscountUpdate);
  document.addEventListener(ThemeEvents.quantitySelectorUpdate, onQuantityUpdate, true);
  document.addEventListener('click', onDrawerClick, true);
  document.addEventListener('pointermove', onPointerMove, { passive: true });
  document.addEventListener('pointerout', onPointerOut, { passive: true });

  const observer = new MutationObserver((mutations) => {
    if (mutations.some((mutation) => mutation.type === 'attributes' && mutation.attributeName === 'open')) {
      handleDrawerState();
    }
    if (
      mutations.some(
        (mutation) =>
          mutation.type === 'childList' && mutation.target instanceof Element && mutationContainsCartRender(mutation)
      ) &&
      getOpenDrawer()
    ) {
      scheduleReconcile();
    }
  });
  observer.observe(document.documentElement, { attributes: true, childList: true, subtree: true, attributeFilter: ['open'] });
  handleDrawerState();
}
